import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/common/PageHeader";
import useStore from "../store/useStore";
import api from "../services/api";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast, user } = useStore();
  const isStaff = user?.role === 'Staff';
  const [now, setNow] = useState(Date.now());

  const [stats, setStats] = useState({
    totalAssets: 0,
    totalRentals: 0,
    totalTenants: 0,
    availableRentals: 0
  });

  const [duePayments, setDuePayments] = useState([]);
  const [availableRentals, setAvailableRentals] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [companySubscription, setCompanySubscription] = useState({
    companyName: '',
    active: 0,
    subscriptionStatus: 'Inactive',
    subscriptionEnd: null
  });

  const formatCurrency = (value) =>
    `GMD ${parseFloat(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const toTimestamp = (value) => {
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const fetchDashboardStats = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/company');
      setStats(res.data.data || {
        totalAssets: 0,
        totalRentals: 0,
        totalTenants: 0,
        availableRentals: 0
      });
    } catch (error) {
      console.error('❌ Dashboard stats error:', error);
      toast(error.response?.data?.message || "Failed to load dashboard stats", "error");
    }
  }, [toast]);

  const fetchCompanySubscription = useCallback(async () => {
    try {
      const res = await api.get('/companies/my-company');
      const data = res.data?.data || {};
      setCompanySubscription({
        companyName: data.companyName || '',
        active: Number(data.active || 0),
        subscriptionStatus: data.subscriptionStatus || 'Inactive',
        subscriptionEnd: data.subscriptionEnd || null
      });
    } catch (error) {
      console.error('❌ Company subscription error:', error);
      toast(error.response?.data?.message || "Failed to load company subscription", "error");
    }
  }, [toast]);

  const fetchDuePayments = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/due-payments');
      setDuePayments(res.data.data || []);
    } catch (error) {
      console.error('❌ Error fetching due payments:', error);
      toast(error.response?.data?.message || "Failed to load due payments", "error");
    }
  }, [toast]);

  const fetchAvailableRentals = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/available-rentals');
      setAvailableRentals(res.data.data || []);
    } catch (error) {
      console.error('❌ Error fetching available rentals:', error);
      toast(error.response?.data?.message || "Failed to load available rentals", "error");
    }
  }, [toast]);

  const fetchRecentActivities = useCallback(async () => {
    try {
      const [paymentsRes, maintenanceRes] = await Promise.all([
        api.get('/payments'),
        api.get('/maintenance')
      ]);

      const payments = (paymentsRes.data.data || []).slice(0, 6).map((payment) => ({
        id: `payment-${payment.id}`,
        type: 'payment',
        title: `Payment received from ${payment.tenantName || 'Tenant'}`,
        description: `${payment.assetName || 'Property'} • ${payment.unitNumber || 'Unit'}`,
        amount: parseFloat(payment.amount || 0),
        status: payment.paymentMethod || 'Recorded',
        at: payment.paymentDate || payment.createdAt
      }));

      const maintenance = (maintenanceRes.data.data || []).slice(0, 6).map((record) => ({
        id: `maintenance-${record.id}`,
        type: 'maintenance',
        title: `${record.maintenanceType || 'Maintenance'} request`,
        description: `${record.assetName || 'Property'} • ${record.location || 'N/A'}`,
        amount: parseFloat(record.cost || 0),
        status: record.status || 'Pending',
        at: record.date || record.createdAt
      }));

      const merged = [...payments, ...maintenance]
        .sort((a, b) => toTimestamp(b.at) - toTimestamp(a.at))
        .slice(0, 8);

      setRecentActivities(merged);
    } catch (error) {
      console.error('❌ Error fetching recent activities:', error);
      setRecentActivities([]);
      toast(error.response?.data?.message || "Failed to load recent activities", "error");
    }
  }, [toast]);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchDashboardStats(),
        fetchDuePayments(),
        fetchAvailableRentals(),
        fetchRecentActivities()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchAvailableRentals, fetchDashboardStats, fetchDuePayments, fetchRecentActivities, toast]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (isStaff) return;
    fetchCompanySubscription();
  }, [fetchCompanySubscription, isStaff]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchAllData();
      toast('Dashboard refreshed', 'success');
    } finally {
      setRefreshing(false);
    }
  };

  const dueAmount = useMemo(
    () => duePayments.reduce((sum, item) => sum + parseFloat(item.pendingBalance || 0), 0),
    [duePayments]
  );

  const subscriptionCountdown = useMemo(() => {
    if (isStaff) return null;

    const isActive = Number(companySubscription.active) === 1 && String(companySubscription.subscriptionStatus || '').toLowerCase() === 'active';
    if (!isActive || !companySubscription.subscriptionEnd) {
      return { label: 'Subscription inactive', tone: 'bg-red-600 text-white border-red-600' };
    }

    const expiryTime = new Date(companySubscription.subscriptionEnd).getTime();
    if (!Number.isFinite(expiryTime)) {
      return { label: 'Expiry unknown', tone: 'bg-amber-100 text-amber-800 border-amber-200' };
    }

    const diffMs = expiryTime - now;
    if (diffMs <= 0) {
      return { label: 'Expired', tone: 'bg-red-600 text-white border-red-600' };
    }

    const totalMinutes = Math.ceil(diffMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    let label = `${minutes}m`;
    if (days > 0) {
      label = `${days}d ${hours}h`;
    } else if (hours > 0) {
      label = `${hours}h ${minutes}m`;
    }

    return { label, tone: 'bg-amber-500 text-white border-amber-500' };
  }, [companySubscription.active, companySubscription.subscriptionEnd, companySubscription.subscriptionStatus, isStaff, now]);

  const kpis = [
    {
      label: 'Assets',
      value: stats.totalAssets,
      icon: <ApartmentOutlinedIcon sx={{ fontSize: 20 }} />,
      color: 'bg-cyan-50 text-cyan-700 border-cyan-200'
    },
    {
      label: 'Active Rentals',
      value: stats.totalRentals,
      icon: <AssignmentOutlinedIcon sx={{ fontSize: 20 }} />,
      color: 'bg-teal-50 text-teal-700 border-teal-200'
    },
    {
      label: 'Tenants',
      value: stats.totalTenants,
      icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />,
      color: 'bg-sky-50 text-sky-700 border-sky-200'
    },
    {
      label: 'Vacant Units',
      value: stats.availableRentals,
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 20 }} />,
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    {
      label: 'Due Accounts',
      value: duePayments.length,
      icon: <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />,
      color: 'bg-amber-50 text-amber-700 border-amber-200'
    },
    {
      label: 'Amount At Risk',
      value: formatCurrency(dueAmount),
      icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 20 }} />,
      color: 'bg-rose-50 text-rose-700 border-rose-200'
    }
  ];

  if (isStaff) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Dashboard"
          action={
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-60"
            >
              <AutorenewOutlinedIcon sx={{ fontSize: 18 }} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          }
        />

        <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {kpis.slice(0, 4).map((kpi) => (
            <div key={kpi.label} className={`rounded-xl border p-4 bg-white shadow-sm ${kpi.color}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide font-semibold">{kpi.label}</p>
                <div>{kpi.icon}</div>
              </div>
              <p className="text-2xl mt-2 font-bold text-gray-900">{kpi.value}</p>
            </div>
          ))}
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-rose-50 border-b border-rose-100">
              <h3 className="text-sm font-bold text-rose-900">Due Rentals (Contract Window)</h3>
              <button
                onClick={() => navigate('/payments')}
                className="text-xs font-semibold text-rose-700 hover:text-rose-800"
              >
                Open Payments
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Tenant</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Property</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Billing</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Loading due rentals...</td></tr>
                  ) : duePayments.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">No rentals are in due window right now.</td></tr>
                  ) : duePayments.slice(0, 8).map((payment) => (
                    <tr key={payment.rentalId} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-800 whitespace-nowrap">{payment.tenantName || 'Unassigned'}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{payment.assetName || 'N/A'} • {payment.unitNumber || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{payment.billingPeriod || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(payment.dueDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${payment.status === 'Overdue' ? 'bg-red-100 text-red-700' : payment.status === 'Due Today' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-emerald-50 border-b border-emerald-100">
              <h3 className="text-sm font-bold text-emerald-900">Available Units</h3>
              <button
                onClick={() => navigate('/rentals')}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Open Rentals
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Property</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Location</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-500">Loading units...</td></tr>
                  ) : availableRentals.length === 0 ? (
                    <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-500">No units available right now.</td></tr>
                  ) : availableRentals.slice(0, 8).map((rental) => (
                    <tr key={rental.id} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-800">{rental.assetName || 'N/A'} • {rental.unitNumber || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-700">{rental.location || 'N/A'}</td>
                      <td className="px-4 py-3 font-semibold text-emerald-700">{formatCurrency(rental.rentalCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        action={
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-60"
            >
              <AutorenewOutlinedIcon sx={{ fontSize: 18 }} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
            <button
              type="button"
              onClick={fetchCompanySubscription}
              className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold shadow-sm ${subscriptionCountdown?.tone || 'bg-amber-100 text-amber-800 border-amber-200'}`}
              title={companySubscription.subscriptionEnd ? `${companySubscription.companyName || 'Company'} expires on ${formatDate(companySubscription.subscriptionEnd)}` : 'Subscription expiry not available'}
            >
              <span className="inline-flex h-2.5 w-2.5 rounded-full bg-white/90"></span>
              {subscriptionCountdown ? `Expiry: ${subscriptionCountdown.label}` : 'Expiry unavailable'}
            </button>
          </div>
        }
      />

      <section className="rounded-2xl bg-gradient-to-r from-teal-800 via-teal-700 to-cyan-700 p-6 md:p-8 text-white shadow-lg">
        <p className="text-xs uppercase tracking-[0.18em] text-teal-100 mb-2">Company Admin Dashboard</p>
        <h2 className="text-2xl md:text-3xl font-bold leading-tight">Dashboard to Monitor Your Business</h2>
        <p className="mt-2 text-sm text-teal-100 max-w-3xl">
          Monitor tenant information, rentals, payments, and maintenances
        </p>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <div key={kpi.label} className={`rounded-xl border p-4 bg-white shadow-sm ${kpi.color}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide font-semibold">{kpi.label}</p>
              <div>{kpi.icon}</div>
            </div>
            <p className="text-2xl mt-2 font-bold text-gray-900">{kpi.value}</p>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-1 gap-6">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
            {loading ? (
              <p className="text-sm text-gray-500">Loading activity...</p>
            ) : recentActivities.length === 0 ? (
              <p className="text-sm text-gray-500">No recent activity found.</p>
            ) : recentActivities.map((activity) => (
              <div key={activity.id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900">{activity.title}</p>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${activity.type === 'payment' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                    {activity.type}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{activity.description}</p>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span>{activity.status}</span>
                  <span>{formatDate(activity.at)}</span>
                </div>
                {activity.amount > 0 && (
                  <p className="text-xs font-semibold text-gray-700 mt-1">{formatCurrency(activity.amount)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 bg-rose-50 border-b border-rose-100">
            <h3 className="text-sm font-bold text-rose-900">Due Rentals (Contract Window)</h3>
            <button
              onClick={() => navigate('/payments')}
              className="text-xs font-semibold text-rose-700 hover:text-rose-800"
            >
              Open Payments
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Tenant</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Property</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Billing</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Due Date</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Loading due rentals...</td></tr>
                ) : duePayments.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">No rentals are in due window right now.</td></tr>
                ) : duePayments.slice(0, 8).map((payment) => (
                  <tr key={payment.rentalId} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">{payment.tenantName || 'Unassigned'}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{payment.assetName || 'N/A'} • {payment.unitNumber || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{payment.billingPeriod || '-'}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(payment.dueDate)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${payment.status === 'Overdue' ? 'bg-red-100 text-red-700' : payment.status === 'Due Today' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                        {payment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 bg-emerald-50 border-b border-emerald-100">
            <h3 className="text-sm font-bold text-emerald-900">Available Units</h3>
            <button
              onClick={() => navigate('/rentals')}
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
            >
              Open Rentals
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Property</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Location</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-600">Rate</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-500">Loading units...</td></tr>
                ) : availableRentals.length === 0 ? (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-500">No units available right now.</td></tr>
                ) : availableRentals.slice(0, 8).map((rental) => (
                  <tr key={rental.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-800">{rental.assetName || 'N/A'} • {rental.unitNumber || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-700">{rental.location || 'N/A'}</td>
                    <td className="px-4 py-3 font-semibold text-emerald-700">{formatCurrency(rental.rentalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;